/**
 * Full-text search over the instruction pages, aware of Finnish inflection.
 *
 * A plain substring search does not work for Finnish: "asetus" never occurs
 * in "asetuksista" or "asetukset", because the stem itself changes when the
 * word is inflected. Each search word is therefore expanded into the stems
 * its inflected forms can start with (asetus -> asetus, asetuks), and a word
 * in the text matches when it starts with one of them. Prefix matching also
 * covers the endings and compounds that start with the word
 * ("asetusmuuttuja"); longer stems also match inside a compound
 * ("perusasetukset"). The part of the text that matched is what gets
 * highlighted, not the search word. Letters are only lower-cased, never
 * folded, so ä and ö stay distinct from a and o.
 */

export interface SearchablePage {
  slug: string;
  title: string;
  headings: string[];
  /** Body text, one entry per paragraph, list item or table cell. */
  blocks: string[];
}

export interface TextSegment {
  text: string;
  hit: boolean;
}

export interface SearchHit {
  slug: string;
  score: number;
  /** True when every search word is in the title. */
  titleMatch: boolean;
  snippet: TextSegment[];
}

interface Range {
  start: number;
  end: number;
  term: number;
}

/** Case, plural and possessive endings, longest first. */
const ENDINGS = [
  'itten',
  'ineen',
  'iden',
  'tten',
  'issa',
  'issä',
  'ista',
  'istä',
  'illa',
  'illä',
  'ilta',
  'iltä',
  'ille',
  'ihin',
  'iksi',
  'itta',
  'ittä',
  'ssa',
  'ssä',
  'sta',
  'stä',
  'lla',
  'llä',
  'lta',
  'ltä',
  'lle',
  'ksi',
  'tta',
  'ttä',
  'den',
  'jen',
  'ien',
  'ina',
  'inä',
  'na',
  'nä',
  'ta',
  'tä',
  'en',
  'in',
  'an',
  'än',
  'on',
  'ön',
  'un',
  'yn',
  'a',
  'ä',
  'n',
  't'
];

/** What a stem must keep after an ending is cut off, so short words stay whole. */
const MIN_STEM = 4;

/** Stems at least this long also match inside a compound word. */
const MIN_INFIX = 5;

/** Consonant gradation, strong grade first: hoito/hoidon, lääke/lääkkeen. */
const GRADATION: [string, string][] = [
  ['kk', 'k'],
  ['pp', 'p'],
  ['tt', 't'],
  ['nt', 'nn'],
  ['nk', 'ng'],
  ['mp', 'mm'],
  ['lt', 'll'],
  ['rt', 'rr'],
  ['t', 'd'],
  ['p', 'v']
];

const VOWELS = 'aeiouyäö';
const WORD = /[\p{L}\p{N}]+/gu;

export function normalize(text: string): string {
  return text.toLocaleLowerCase('fi');
}

export function searchTerms(query: string): string[] {
  const terms: string[] = normalize(query).match(WORD) ?? [];
  return terms.filter((t, i) => terms.indexOf(t) === i);
}

/**
 * The stems the inflected forms of `term` can start with, longest first.
 * Generous on purpose: an extra stem costs a few loose matches lower in the
 * list, a missing one hides the page the user is looking for.
 */
export function stemVariants(term: string): string[] {
  const word = normalize(term);
  const stems = new Set<string>([word]);
  for (const ending of ENDINGS) {
    if (word.endsWith(ending) && word.length - ending.length >= MIN_STEM) {
      stems.add(word.slice(0, -ending.length));
    }
  }

  const variants = new Set<string>();
  for (const stem of stems) {
    variants.add(stem);
    // asetukse(t) / asetuks(ista) -> asetus
    if (stem.endsWith('kse')) {
      variants.add(stem.slice(0, -3) + 's');
      variants.add(stem.slice(0, -1));
    } else if (stem.endsWith('ks') && stem.length > MIN_STEM) {
      variants.add(stem.slice(0, -2) + 's');
    }
    // -us/-ys/-os/-is nouns inflect with -ks-: asetus -> asetuksen.
    // -as/-es/-is lose the s: potilas -> potilaan.
    const last2 = stem.slice(-2);
    if (
      stem.length >= MIN_STEM &&
      last2[1] === 's' &&
      VOWELS.includes(last2[0])
    ) {
      if ('uyoöi'.includes(last2[0])) {
        variants.add(stem.slice(0, -1) + 'ks');
      }
      if ('aäei'.includes(last2[0])) {
        variants.add(stem.slice(0, -1));
      }
    }
    // Final vowels may change and the consonant before them carry
    // gradation: hoito -> hoidon, lääkkee(t) -> lääke.
    const base = stem.replace(/[aeiouyäö]+$/u, '');
    if (stem.length > MIN_STEM && base !== stem && base.length >= 3) {
      variants.add(base);
      gradationVariants(base).forEach((v) => variants.add(v));
    }
  }
  return [...variants]
    .filter((v) => v.length >= Math.min(3, word.length))
    .sort((a, b) => b.length - a.length);
}

function gradationVariants(stem: string): string[] {
  const out: string[] = [];
  for (const [strong, weak] of GRADATION) {
    if (stem.endsWith(strong)) {
      out.push(stem.slice(0, -strong.length) + weak);
      break;
    }
  }
  for (const [strong, weak] of GRADATION) {
    if (stem.endsWith(weak) && !stem.endsWith(strong)) {
      out.push(stem.slice(0, -weak.length) + strong);
      break;
    }
  }
  return out;
}

/** Where in `text` each search term matches, in text order. */
export function findMatches(text: string, termVariants: string[][]): Range[] {
  const ranges: Range[] = [];
  for (const m of text.matchAll(WORD)) {
    const word = normalize(m[0]);
    termVariants.forEach((variants, term) => {
      const hit = matchInWord(word, variants);
      if (hit) {
        ranges.push({
          start: m.index! + hit.offset,
          end: m.index! + hit.offset + hit.length,
          term
        });
      }
    });
  }
  return ranges;
}

function matchInWord(
  word: string,
  variants: string[]
): { offset: number; length: number } | null {
  // Variants are longest first, so the first hit highlights the most.
  for (const v of variants) {
    if (word.startsWith(v)) {
      return { offset: 0, length: v.length };
    }
  }
  for (const v of variants) {
    if (v.length >= MIN_INFIX) {
      const offset = word.indexOf(v);
      if (offset > 0) {
        return { offset, length: v.length };
      }
    }
  }
  return null;
}

const TITLE_WEIGHT = 100;
const HEADING_WEIGHT = 10;
const MAX_HEADING_HITS = 4;
const MAX_BODY_HITS = 20;

/**
 * Pages that contain every search word, best first. Title matches always
 * rank above pages that only mention the words in their text; within the
 * same score the catalog order is kept.
 */
export function searchPages(
  pages: SearchablePage[],
  query: string
): SearchHit[] {
  const terms = searchTerms(query);
  if (!terms.length) {
    return [];
  }
  const termVariants = terms.map(stemVariants);
  const hits: (SearchHit & { order: number })[] = [];

  pages.forEach((page, order) => {
    const found = new Set<number>();
    const titleTerms = new Set(
      findMatches(page.title, termVariants).map((r) => r.term)
    );
    titleTerms.forEach((t) => found.add(t));

    let headingHits = 0;
    for (const heading of page.headings) {
      const ranges = findMatches(heading, termVariants);
      ranges.forEach((r) => found.add(r.term));
      headingHits += ranges.length ? 1 : 0;
    }

    let bodyHits = 0;
    let snippetBlock: { text: string; ranges: Range[] } | null = null;
    let snippetTerms = 0;
    for (const block of page.blocks) {
      const ranges = findMatches(block, termVariants);
      if (!ranges.length) {
        continue;
      }
      ranges.forEach((r) => found.add(r.term));
      bodyHits += ranges.length;
      const distinct = new Set(ranges.map((r) => r.term)).size;
      if (distinct > snippetTerms) {
        snippetBlock = { text: block, ranges };
        snippetTerms = distinct;
      }
    }

    if (found.size < terms.length) {
      return;
    }

    const score =
      titleTerms.size * TITLE_WEIGHT +
      Math.min(headingHits, MAX_HEADING_HITS) * HEADING_WEIGHT +
      Math.min(bodyHits, MAX_BODY_HITS);

    let snippet: TextSegment[];
    if (snippetBlock) {
      snippet = buildSnippet(snippetBlock.text, snippetBlock.ranges);
    } else {
      const heading = page.headings.find(
        (h) => findMatches(h, termVariants).length
      );
      const text = heading ?? page.blocks[0] ?? '';
      snippet = buildSnippet(text, findMatches(text, termVariants));
    }

    hits.push({
      slug: page.slug,
      score,
      titleMatch: titleTerms.size === terms.length,
      snippet,
      order
    });
  });

  return hits
    .sort((a, b) => b.score - a.score || a.order - b.order)
    .map(({ order, ...hit }) => hit);
}

const SNIPPET_LENGTH = 160;
const SNIPPET_LEAD = 50;

/**
 * About SNIPPET_LENGTH characters of `text` around its first match, cut at
 * word boundaries, with the matched parts marked.
 */
export function buildSnippet(text: string, ranges: Range[]): TextSegment[] {
  let start = 0;
  let end = text.length;
  if (text.length > SNIPPET_LENGTH) {
    const first = ranges[0]?.start ?? 0;
    if (first > SNIPPET_LEAD) {
      // Start at the sentence the match is in, if it starts close enough.
      const sentence = text.lastIndexOf('. ', first);
      start =
        sentence >= 0 && first - sentence <= SNIPPET_LEAD + 30
          ? sentence + 2
          : wordStart(text, first - SNIPPET_LEAD);
    }
    end = Math.min(text.length, wordEnd(text, start + SNIPPET_LENGTH));
  }

  const segments: TextSegment[] = [];
  const push = (part: string, hit: boolean) => {
    if (part) {
      segments.push({ text: part, hit });
    }
  };
  if (start > 0) {
    push('…', false);
  }
  let pos = start;
  for (const r of ranges) {
    if (r.start < pos || r.end > end) {
      continue;
    }
    push(text.slice(pos, r.start), false);
    push(text.slice(r.start, r.end), true);
    pos = r.end;
  }
  push(text.slice(pos, end), false);
  if (end < text.length) {
    push('…', false);
  }
  return segments;
}

function wordStart(text: string, index: number): number {
  const space = text.indexOf(' ', Math.max(0, index));
  return space < 0 ? Math.max(0, index) : space + 1;
}

function wordEnd(text: string, index: number): number {
  if (index >= text.length) {
    return text.length;
  }
  const space = text.lastIndexOf(' ', index);
  return space <= 0 ? index : space;
}
